import { Share } from '@capacitor/share';
import { useApp } from '@/lib/core/providers/appProvider';
import { useClientTranslation } from '@/lib/core/i18n';
import { getShareCode } from '@/services/platform/data/booking';
import { getConfig } from '@/../config/environment';

export interface ShareContentOptions {
  title: string;
  url: string;
  dialogTitle?: string;
}

/**
 * Replaces the origin (protocol + host + port) of `url` with the configured `shareUrl`.
 * Path, query and hash are preserved. Returns `url` unchanged when `shareUrl` is not
 * configured or either value is not a valid absolute URL.
 */
export function toShareUrl(url: string): string {
  const { shareUrl } = getConfig();
  if (!shareUrl) return url;
  try {
    // Rebuild rather than mutate: the `host` setter keeps the old port, and `protocol`
    // can't switch between special (https) and non-special (capacitor) schemes.
    const { pathname, search, hash } = new URL(url);
    return new URL(`${pathname}${search}${hash}`, new URL(shareUrl).origin).toString();
  } catch {
    return url;
  }
}

export async function shareContent({ title, url, dialogTitle }: ShareContentOptions): Promise<void> {
  // Web Share API. If called after an async operation the gesture may have expired;
  // NotAllowedError is caught and falls through to the clipboard fallback.
  if (navigator.share) {
    try {
      await navigator.share({ title, url });
      return;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (err instanceof Error && err.name !== 'NotAllowedError') throw err;
    }
  }

  // Capacitor Share plugin (native only — UIActivityViewController / Intent.ACTION_SEND).
  // Share.canShare() is async but the gesture restriction does not apply on native.
  // Android rejects non-http/file URLs with "Unsupported url"; pass those as text instead.
  try {
    const { value: canShare } = await Share.canShare();
    if (canShare) {
      const isWebUrl = /^https?:\/\//i.test(url) || /^file:\/\//i.test(url);
      await Share.share({
        title,
        ...(isWebUrl ? { url } : { text: url }),
        dialogTitle: dialogTitle ?? title,
      });
      return;
    }
  } catch (err) {
    console.warn('Share plugin unavailable:', err instanceof Error ? err.message : String(err));
  }

  // Fallback — clipboard
  await navigator.clipboard.writeText(url);
}

interface ShareableReservation {
  publicGuid?: string | null;
  venue?: { name?: string | null } | null;
  name?: string | null;
}

export function useBookingShare(reservation: ShareableReservation) {
  const translation = useClientTranslation();
  const { appSettings } = useApp();

  async function handleShare() {
    const title = reservation.venue?.name ?? reservation.name ?? translation('App.Labels.Booking');
    const bookingDetailPath = appSettings?.properties?.bookingDetail?.route?.path?.replace(/^\//, '');
    const guid = reservation.publicGuid;

    let url = window.location.href;

    if (bookingDetailPath && guid) {
      const { shareUrl, appUrl } = getConfig();
      const baseUrl = shareUrl || appUrl || window.location.origin;
      url = `${baseUrl}/${bookingDetailPath}${guid}`;
      const sharedCode = await getShareCode(guid);
      if (sharedCode?.code) {
        url = `${baseUrl}/${bookingDetailPath}${guid}/${sharedCode.code}`;
      }
    }

    await shareContent({ title, url });
  }

  return { handleShare };
}
